import React from 'react';

interface WidgetStyles {
  chatBackgroundColor: string;
  chatBorderColor: string;
  chatBorderRadius: number;
  chatWidth: number;
  chatHeight: number;
  headerBackgroundColor: string;
  headerColor: string;
  botBackgroundColor: string;
  botColor: string;
  botAvatarBackgroundColor: string;
  botAvatarColor: string;
  botAvatarSize: number;
  sendButtonBackgroundColor: string;
  sendButtonColor: string;
  inputAreaBackgroundColor?: string;
  userBackgroundColor: string;
  userColor: string;
}

interface WidgetText {
  headerTitle: string;
  botMessage: string;
  inputPlaceholder: string;
  sendButtonText: string;
  emailInfoText: string;
}

interface AdeprimoWidgetProps {
  styles: WidgetStyles;
  textContent: WidgetText;
}

export function AdeprimoWidget({ styles, textContent }: AdeprimoWidgetProps) {
  return (
    <div
      className="chatbot-container"
      style={{
        width: `${styles.chatWidth}px`,
        height: `${styles.chatHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 5px 20px rgba(0, 0, 0, 0.15)',
        borderRadius: '15px',
        overflow: 'hidden',
        backgroundColor: '#ffffff'
      }}
    >
      {/* Header section with title and info text */}
      <header className="chatbot-header" style={{
        backgroundColor: styles.headerBackgroundColor || '#212450',
        color: styles.headerColor || 'white',
        padding: '20px',
        textAlign: 'center',
        flexShrink: 0
      }}>
        <h1 style={{
          margin: '0 0 10px',
          fontSize: '1.5rem',
          fontWeight: 600,
          color: styles.headerColor || 'white'
        }}>
          {textContent.headerTitle}
        </h1>
        <p style={{
          margin: 0,
          fontSize: '0.9rem',
          lineHeight: 1.4,
          color: styles.headerColor || 'rgba(255, 255, 255, 0.9)'
        }}>
          {textContent.emailInfoText}
        </p>
      </header>

      {/* The scrolling area where messages appear */}
      <main className="chat-area" style={{
        flexGrow: 1,
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
      }}>
        <div className="message-container" style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          maxWidth: '90%'
        }}>
          {/* The circular icon/avatar for the bot */}
          <div
            className="avatar"
            style={{
              width: `${styles.botAvatarSize}px`,
              height: `${styles.botAvatarSize}px`,
              borderRadius: '50%',
              flexShrink: 0,
              backgroundColor: styles.botAvatarBackgroundColor || '#212450',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 13c1.65 0 3-1.35 3-3S8.65 7 7 7s-3 1.35-3 3 1.35 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z" fill={styles.botAvatarColor || '#FFFFFF'} />
            </svg>
          </div>

          {/* The message bubble styling */}
          <div className="message-bubble" style={{
            backgroundColor: styles.botBackgroundColor || '#f8f9fa',
            color: styles.botColor || '#222',
            padding: '12px 16px',
            borderRadius: '12px',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {textContent.botMessage}
          </div>
        </div>
        
        {/* User response message */}
        <div className="message-container" style={{
          display: 'flex',
          justifyContent: 'flex-end',
          maxWidth: '85%',
          marginLeft: 'auto'
        }}>
          <div className="message-bubble" style={{
            backgroundColor: styles.userBackgroundColor || '#f0f0f0',
            color: styles.userColor || '#000000',
            padding: '12px 16px',
            borderRadius: '12px',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            Thanks! I have a question about check-in.
          </div>
        </div>
      </main>

      {/* The bottom area with the text input and send button */}
      <footer className="input-area" style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: styles.inputAreaBackgroundColor || '#f8f9fa'
      }}>
        <input
          type="text"
          placeholder={textContent.inputPlaceholder}
          style={{
            flexGrow: 1,
            border: 'none',
            borderRadius: '8px',
            padding: '10px 16px',
            fontSize: '1rem',
            outline: 'none',
            marginRight: '12px'
          }}
        />
        <button className="send-button" aria-label="Send message" style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{
            width: '24px',
            height: '24px',
            fill: styles.sendButtonColor || '#777777'
          }}>
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2 .01 7z"></path>
          </svg>
        </button>
      </footer>
    </div>
  );
}
